<style lang="scss" scoped>
@include component(match) {
	@include part(header) {
		@include part(steps) {
			margin-top: 20px;
		}
		@include part(options) {
			margin-left: auto;
			> div {
				margin-right: 15px;
				font-size: 100%;
			}
		}
	}
	@include part(description) {
		padding: 0;
		margin: 0;
	}
}
.match {
	&__footer {
		padding: 30px 40px;
		display: block;

		&__pagination {
			display: flex;
			justify-content: space-between;
			margin-bottom: 30px;
		}

		&__actions {
			display: flex;
			justify-content: flex-end;
		}
	}
}
.classes {
	&__header {
		@include flex-center;
		&__options {
			margin-left: auto;
			> div {
				margin-right: 15px;
				font-size: 100%;
			}
		}
	}
}
</style>
<template lang="pug">
.match
	app-header
		.classes__header(slot="header", v-if="currentRouteName === 'matching.classes.prediction'")
			h1 Match classes
			.classes__header__options
				template(v-if="paginated.length > 0")
					el-button.mega-action(type="primary", @click="refetchClasses",  style="margin-left:15px") Refetch classes
					el-button.mega-action(v-if="paginated && ignored" @click="ignoreAll(false)") Un-Ignore all
					el-button.mega-action(v-if="paginated && !ignored" @click="ignoreAll(true)") Ignore all
					el-button.mega-action(type="success", @click="saveByBatch", :loading="saveLoading") Save
		.match__header(slot="header", v-else)
			h1 Connect to {{ providerFullName }}
			.match__header__steps
				el-steps(:active="3", finish-status="success")
					el-step(title="Connect")
					el-step(title="Settings")
					el-step(title="Match")
					el-step(title="Classes")
	.container
		.main-panel.directory
			loading(v-if="$apollo.loading || classesLoading")
			template(v-else)
				.main-panel__padding
					p.match__description 
						| Below are predicted matches for the data stored in Edusync compared to the classes already in your {{ providerName }} account.
						| Please review these, and click <b>Save</b> once you are happy that you have ignored any incorrect matches.
						| You will be able to work with ignored matches once setup is complete.
				
				el-table(:data="paginated").custom-table.overflow
					el-table-column(prop="name" label="Edusync class")    
						template(slot-scope="scope") 
							span {{ scope.row.name }}
					el-table-column(prop="type" label="Type")
						template(slot-scope="scope") 
							span {{ scope.row.type | capitalize }}
					el-table-column(:label="`${providerName} Class`" )
						template(slot-scope="scope")
							span {{ scope.row.spName | capitalize }}
					el-table-column(prop="score", sortable="", label="Prediction strength")
						template(slot-scope="scope")
								el-progress(:percentage="getMatchScore(scope.row.score)", :status="scope.row.score > .49 || isIgnored(scope) ? 'exception' : 'success'")
					el-table-column(label="Action" width="130")
						template(slot-scope="scope")
							el-button(:type="isIgnored(scope) ? 'danger' : 'secondary'" @click="scope.row.assigned = scope.row.assigned === 'ignore' ? '' : 'ignore'", size="small" ) {{ isIgnored(scope) ? 'Ignored' : 'Ignore' }}
				
				.match__footer
					.match__footer__pagination(v-if="paginated.length > 0")
						.match__pagination__left
							el-pagination(background='', layout='prev, pager, next', :current-page.sync='page', :page-size='per_page', :total='classMatched.length')
						.match__pagination__right
							| Showing
							strong &nbsp;{{ paginated.length }}
							| &nbsp;out of a possible
							strong &nbsp;{{ classMatched.length }}
							| &nbsp;results	
					.match__footer__actions(v-if="currentRouteName !== 'matching.classes.prediction'")
						template(v-if="paginated.length > 0")
							el-button.mega-action(type="primary", @click="refetchClasses",  style="margin-left:15px") Refetch classes
							el-button.mega-action(v-if="paginated && ignored" @click="ignoreAll(false)") Un-Ignore all
							el-button.mega-action(v-if="paginated && !ignored" @click="ignoreAll(true)") Ignore all
							el-button.mega-action(type="success", @click="saveByBatch", :loading="saveLoading") Save
						template(v-else)
							el-button.ml-auto.mega-action(type="success", @click="wizardComplete", :loading="saveLoading") Done
</template>
<script>
import gql from "graphql-tag";
import _ from "lodash";
import Fuse from "fuse.js";
export default {
	data() {
		return {
			classes: [],
			thirdPartyClasses: [],
			saveLoading: false,
			page: 1,
			per_page: 50,
			ignored: false,
		};
	},

	computed: {
		inputClasses() {
			return _(this.classMatched)
				.map(cls => {
					return {
						wondeId: cls.id,
						wondeIdType: cls.type,
						providerId: cls.assigned
					};
				})
				.value();
		},

		paginated() {
			return !_.isEmpty(this.classMatched) ? _.chunk(this.classMatched, this.per_page)[this.page - 1] : [];
		},

		classesLoading() {
			return _.isEmpty(this.thirdPartyClasses);
		},

		classMatched() {
			const options = {
				shouldSort: true,
				includeScore: true,
				keys: ["name"],
				threshold: 0.5,
			};

			// No classes were found, so no matching can happen
			if(this.thirdPartyClasses.length === 1 && this.thirdPartyClasses[0].id === "") {
				return [];
			}

			let fuse = new Fuse(this.thirdPartyClasses, options);

			return _.compact(
				this.classes.map(({ name, calculated_name, ...wClass } = wClass) => {
					const search = fuse
						.search(calculated_name)
						.slice(0, this.optionLimit)
						.shift(); // search thirdparty classes

					let { status, providerId } = _.find(this.matchedClasses, { wondeId: wClass.id, wondeIdType: wClass.type }) || {}; // searched existing matched class

					return {
						...wClass,
						spName: search ? search.item.name : "",
						name,
						assigned: status ? (status === "ignored" ? "ignore" : providerId) : (search ? search.item.id : "ignore"),
						score: search ? search.score : 1
					};
				})
			);
		},

		currentRouteName() {
			// Access the route name using this.$route.name
			return this.$route.name;
		}
	},

	methods: {
		async saveMatches(input) {
			try {
				await this.$apollo
					.mutate({
						mutation: gql`
							mutation MatchClasses($input: [MatchClassInput!]!) {
								MatchClasses(input: $input) {
									id
									providerId
									wondeId
									wondeIdType
								}
							}
						`,
						variables: {
							input
						}
					});
				return true;
			} catch (error) {
				return false;
			}
		},

		async saveByBatch() {
			this.saveLoading = true;
			const perPage = 10;
			let curPage = 1;
			let promises = [];
			let chunk = _.chunk(this.inputClasses, perPage)[curPage - 1];
			
			while(chunk) {
				promises.push(this.saveMatches(chunk));
				curPage++;
				chunk = _.chunk(this.inputClasses, perPage)[curPage - 1];
			}

			Promise.all(promises).then((values) => {
				if (values.includes(false)) {
					this.$message.error("Some matches failed to save. Please contact support");
				}
				
				this.wizardComplete();
				this.saveLoading = false;
			});
		},

		getMatchScore(score) {
			return 100 - score * 100;
		},

		ignoreAll(ignore) {
			this.ignored = ignore;
			this.classMatched.map(cls => (cls.assigned = ignore ? "ignore" : ""));
		},

		isIgnored({ row: { assigned } }) {
			return assigned === "ignore" || false;
		},

		wizardComplete() {
			this.$apollo
				.mutate({
					mutation: gql`
						mutation {
							WizardStatus(complete: true) {
								id
								has_completed_wizard
							}
						}
					`
				})
				.then(r => {
					return this.$router.push({ name: "directory" });
				});
		},

		async refetchClasses() {
			this.$confirm(
					`Re-fetching classes will take some time. Are you sure you want to proceed?`,
					"Warning",
					{
						confirmButtonText: "OK",
						cancelButtonText: "Cancel",
						type: "warning"
					}
			).then(async () => {
				try {
					await this.$apollo.mutate({
						mutation: gql`
							mutation CacheClear($item: CacheTypeEnum!) {
								CacheClear(item: $item)
							}`,
							variables: {
								item: "classes"
							}
					});
					this.$apollo.queries.thirdPartyClasses.skip = false
    				await this.$apollo.queries.thirdPartyClasses.refetch()
				} catch (e) {
					this.$message.error("Failed to re-fetch classes");
				}
			})
		}
	},

	apollo: {
		matchedClasses: {
			query: gql`
				{
					MatchedClasses {
						id
						status
						providerId
						wondeId
						wondeIdType
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r => r.MatchedClasses
		},

		thirdPartyClasses: {
			query: gql`
				{
					ThirdPartyClasses {
						id
						name
					}
				}
			`,
			fetchPolicy: "no-cache",
			pollInterval: 5000,
			update: r => r.ThirdPartyClasses,
			skip() {
				return !this.classesLoading;
			}
		},

		classes: {
			query: gql`
				{
					Classes {
						id
						name
						type
						calculated_name
					}
				}
			`,
			fetchPolicy: "no-cache",
			update: r =>
				r.Classes
					? _(r.Classes)
							.map(cls => ({
								...cls,
								assigned: null
							}))
							.sortBy("name")
							.value()
					: []
		}
	}
};
</script>
